import { prepareServiceConfig } from '../common/config'
import { type ServiceConfigsValues } from '../common/constants'
import { FlowrunnerContext, type FlowrunnerContextOptions } from '../common/context'
import type { FilesApi } from '../common/context-files'
import type { ConfigItem } from '../common/types'
import {
  type MethodRequestArgumentsList,
  type MethodRequestArgumentsMap,
  OAUTH_ACCESS_TOKEN_HEADER,
} from './constants'
import { createFlowrunnerSandboxV1 } from './namespace'

function requireService(serviceId: string, serviceEntryPointPath: string) {
  const registered = createFlowrunnerSandboxV1(serviceId, () => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    require(serviceEntryPointPath)
  })

  if (!registered?.ServiceClass) {
    throw new Error(`There is no registered ServiceClass serviceEntryPointPath="${ serviceEntryPointPath }"`)
  }

  return registered
}

type CreateRuntimeContextData = {
  serverURL: string,
  appId: string,
  apiKey: string,
  //
  runtimeKey: string,
  //
  flowId: string,
  executionId: string
  //
  accessToken?: string
  //
  retrieveNewS3Token: (runtimeKey: string) => Promise<{
    filesS3Token: string
  }>
  //
  createFiles?: (options: FlowrunnerContextOptions) => FilesApi

}

// `flowrunner` is typed by the surface a service actually touches — `this.flowrunner.Files` — rather than by
// the concrete FlowrunnerContext, so a host-supplied client (see `createFiles`) is assignable here too.
type FlowrunnerRuntimeContext = {
  flowrunner: { Files: FilesApi }
  httpHeaders: {
    [OAUTH_ACCESS_TOKEN_HEADER]?: string
  }
}

function createRuntimeContext(data: CreateRuntimeContextData): FlowrunnerRuntimeContext {
  const { serverURL, appId, apiKey, runtimeKey, flowId, executionId, accessToken, retrieveNewS3Token } = data

  const filesOptions: FlowrunnerContextOptions = {
    serverURL,
    appId,
    apiKey,
    //
    runtimeKey,
    //
    flowId,
    executionId,
    //
    retrieveNewS3Token,
  }

  // A v1 service has no `usesFileStorage` opt-in — `this.flowrunner.Files` is always live — so a
  // host-supplied client replaces it unconditionally. (v2 consults `createFiles` only when the extension
  // declares `usesFileStorage`, because that is what decides between a real client and the throwing proxy.)
  const flowrunnerContext: FlowrunnerRuntimeContext['flowrunner'] = data.createFiles
    ? { Files: data.createFiles(filesOptions) }
    : new FlowrunnerContext(filesOptions)

  const httpHeaders: FlowrunnerRuntimeContext['httpHeaders'] = {}

  if (accessToken) {
    httpHeaders[OAUTH_ACCESS_TOKEN_HEADER] = accessToken
  }

  return {
    httpHeaders,
    flowrunner: flowrunnerContext,
  }
}

function initServiceInstance(
  serviceId: string,
  serviceEntryPointPath: string,
  configs: ServiceConfigsValues | undefined,
  context: FlowrunnerRuntimeContext
) {
  const { ServiceClass, configItems } = requireService(serviceId, serviceEntryPointPath)

  const config = prepareServiceConfig(configItems, configs)

  const service = new ServiceClass(config, context)

  Object.assign(service, {
    config,
    flowrunner: context.flowrunner,
    request: {
      context,
      headers: context.httpHeaders,
    },
  })

  return service
}

export function getServiceConfigs(serviceId: string, serviceEntryPointPath: string): ConfigItem[] {
  const { configItems } = requireService(serviceId, serviceEntryPointPath)

  return configItems
}

type RunServiceMethodData = {
  serviceId: string,
  serviceEntryPointPath: string,
  configs?: ServiceConfigsValues
  methodName: string
  methodData?: MethodRequestArgumentsMap,
  methodArgs?: MethodRequestArgumentsList
  retrieveNewS3Token: (runtimeKey: string) => Promise<{
    filesS3Token: string
  }>
  // Replaces `this.flowrunner.Files` for this invocation. The default talks to the console over HTTP; a host
  // may build its own against different storage (a legacy-service test mocking the Files API, an e2e harness
  // pointing at a local S3) without the runner or the service knowing.
  createFiles?: (options: FlowrunnerContextOptions) => FilesApi
  executionContext: {
    runtimeKey: string
    accessToken: string
    appId: string
    apiKey: string
    serverURL: string
    flowId: string
    executionId: string
  }
}

export async function runServiceMethod(data: RunServiceMethodData): Promise<unknown> {
  const {
    serviceId,
    serviceEntryPointPath,
    configs,
    methodName,
    methodArgs,
    retrieveNewS3Token,
    createFiles,
  } = data
  const { accessToken, runtimeKey, appId, apiKey, serverURL, flowId, executionId } = data.executionContext

  const context = createRuntimeContext({
    serverURL,
    appId,
    apiKey,
    //
    runtimeKey,
    //
    flowId,
    executionId,
    //
    accessToken,
    //
    retrieveNewS3Token,
    //
    createFiles,
  })

  const serviceInstance = initServiceInstance(serviceId, serviceEntryPointPath, configs, context)

  const invoke = (serviceInstance as Record<string, (...args: unknown[]) => unknown>)[methodName]

  return invoke.apply(serviceInstance, methodArgs || [])
}

